import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TestTube, 
  Phone, 
  Home, 
  Clock, 
  Star,
  CheckCircle,
  Calendar,
  MapPin,
  Percent,
  Shield,
  Award
} from 'lucide-react';
import { mockLabPackages, generateWhatsAppLink, mockUser } from '../mock/mockData';

const Lab = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  const handleBookTest = (labPackage) => {
    // Get saved addresses for delivery
    const savedAddresses = localStorage.getItem('geeth365_addresses');
    const addresses = savedAddresses ? JSON.parse(savedAddresses) : [];
    const defaultAddress = addresses.find(addr => addr.isDefault) || addresses[0];
    
    const addressText = defaultAddress 
      ? `${defaultAddress.formattedAddress}` 
      : 'Please add delivery address in Account section';
    
    const whatsAppLink = generateWhatsAppLink('lab', {
      userName: mockUser.name,
      packageName: labPackage.name,
      preferredDate: selectedDate || 'Tomorrow morning',
      address: addressText
    });
    window.open(whatsAppLink, '_blank');
  };

  const timeSlots = [
    '8:00 - 10:00 AM',
    '10:00 - 12:00 PM', 
    '2:00 - 4:00 PM',
    '4:00 - 6:00 PM'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-600 to-purple-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Lab Tests & Health Packages
              </h1>
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <Percent className="w-6 h-6 text-purple-200" />
                  <span className="text-xl">50% OFF for subscribers</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Home className="w-6 h-6 text-purple-200" />
                  <span className="text-xl">Free home sample collection</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-purple-200" />
                  <span className="text-xl">Cash on Delivery (COD)</span>
                </div>
              </div>
              <Badge className="bg-white text-purple-700 text-lg px-4 py-2 font-semibold">
                NABL Certified Lab
              </Badge>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1602052577122-f73b9710adba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxsYWJvcmF0b3J5fGVufDB8fHx8MTc1ODgxOTkzMHww&ixlib=rb-4.1.0&q=85"
                alt="Laboratory"
                className="w-full h-80 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-purple-200">
            <CardHeader className="bg-purple-50">
              <CardTitle className="flex items-center space-x-2 text-purple-700">
                <Calendar className="w-5 h-5" />
                <span>Schedule Your Test</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date</label>
                  <Input 
                    type="date" 
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time Slot</label>
                  <Select value={selectedTime} onValueChange={setSelectedTime}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Collection Address</label>
                  <div className="flex items-center space-x-2 p-2 border rounded-md bg-gray-50">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">Home (10-68, PM Palem)</span>
                  </div>
                </div>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  Update Preferences
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Lab Packages */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Health Packages</h2>
            <p className="text-xl text-gray-600">Choose from our comprehensive range of health checkups</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mockLabPackages.map((labPackage) => (
              <Card key={labPackage.id} className="hover:shadow-xl transition-all duration-300 border-purple-100">
                <div className="relative">
                  <img 
                    src={labPackage.image}
                    alt={labPackage.name}
                    className="w-full h-48 object-cover rounded-t-lg"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-green-500 text-white">
                      <Award className="w-3 h-3 mr-1" />
                      NABL Certified
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{labPackage.name}</h3>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {labPackage.tests.slice(0, 4).map((test, index) => (
                        <Badge key={index} variant="outline" className="text-xs border-purple-200 text-purple-700">
                          {test}
                        </Badge>
                      ))}
                      {labPackage.tests.length > 4 && (
                        <Badge variant="outline" className="text-xs border-purple-200 text-purple-700">
                          +{labPackage.tests.length - 4} more
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="mb-6">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-3xl font-bold text-gray-900">₹{labPackage.subscriberPrice}</span>
                      <span className="text-lg text-gray-500 line-through">₹{labPackage.mrp}</span>
                      <Badge className="bg-orange-600 text-white">
                        50% OFF
                      </Badge>
                    </div>
                    <p className="text-sm text-green-600 font-medium">
                      ✓ Subscriber price (Free subscription included)
                    </p>
                  </div>

                  {/* Features */}
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>{labPackage.fasting ? 'Fasting required' : 'No fasting required'}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4 text-blue-500" />
                      <span>Reports in {labPackage.tat} hours</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Home className="w-4 h-4 text-purple-500" />
                      <span>Free home sample collection</span>
                    </div>
                  </div>

                  {/* Action Button */}
                  <Button 
                    onClick={() => handleBookTest(labPackage)}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-lg py-3"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Book Test on WhatsApp
                  </Button>

                  {/* Additional Info */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-medium">4.9</span>
                        <span className="text-gray-500">(2.1k reviews)</span>
                      </div>
                      <span className="text-gray-600">Tests included: {labPackage.tests.length}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Popular Individual Tests */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Popular Individual Tests</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: 'Complete Blood Count (CBC)', price: 200, subscriberPrice: 100 },
                { name: 'Liver Function Test (LFT)', price: 400, subscriberPrice: 200 },
                { name: 'Kidney Function Test (KFT)', price: 350, subscriberPrice: 175 },
                { name: 'Vitamin D Total', price: 800, subscriberPrice: 400 },
                { name: 'Vitamin B12', price: 600, subscriberPrice: 300 },
                { name: 'Iron Studies', price: 500, subscriberPrice: 250 }
              ].map((test, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow border-purple-100">
                  <CardContent className="p-4">
                    <h4 className="font-medium text-gray-900 mb-2">{test.name}</h4>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-lg font-bold text-gray-900">₹{test.subscriberPrice}</span>
                        <span className="text-sm text-gray-500 line-through ml-2">₹{test.price}</span>
                      </div>
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                        Book Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Info Section */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-6 text-center">
                <Home className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Home Collection</h3>
                <p className="text-gray-600 text-sm">Trained phlebotomists will collect samples from your home</p>
              </CardContent>
            </Card>
            
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-6 text-center">
                <Shield className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Quality Assured</h3>
                <p className="text-gray-600 text-sm">NABL certified lab with highest quality standards</p>
              </CardContent>
            </Card>
            
            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="p-6 text-center">
                <Clock className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Fast Reports</h3>
                <p className="text-gray-600 text-sm">Get your reports digitally within 6-24 hours</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Lab;